import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { fetchStoreConfig, formatNZD } from "@/lib/api";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchStoreConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  const hasKit = items.some((i) => (i.badge ?? "Combo Kit") === "Combo Kit");
  const shipping = config
    ? hasKit
      ? config.shipping_flat_rate
      : config.accessory_shipping_flat
    : null;

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <section className="py-16 sm:py-20">
        <p className="eyebrow mb-5 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-volt" />
          Your Cart
        </p>
        <h1 className="font-display text-4xl font-black tracking-tight text-ink sm:text-5xl" data-testid="cart-heading">
          THE BUILD LIST.
        </h1>
      </section>

      {items.length === 0 ? (
        <div className="flex min-h-[40vh] flex-col items-start justify-center gap-6 rounded-2xl border border-black/10 bg-surface p-10" data-testid="cart-empty">
          <ShoppingCart className="h-10 w-10 text-zinc-400" />
          <div>
            <h2 className="font-display text-2xl font-extrabold text-ink">Your cart is empty.</h2>
            <p className="mt-2 text-sm text-zinc-600">
              Configure a combo and get rolling.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/shop" data-testid="cart-empty-shop" className="rounded-[3px] bg-volt px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-white hover:bg-volt-dim transition-colors duration-200">
              Shop Kits
            </Link>
            <Link to="/accessories" data-testid="cart-empty-accessories" className="rounded-[3px] border border-black/25 px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-ink hover:border-volt hover:text-volt transition-colors duration-200">
              Browse Accessories
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2" data-testid="cart-items">
            {items.map((item, i) => (
              <Reveal key={item.key} delay={i * 0.05}>
                <div className="flex gap-4 rounded-2xl border border-black/10 bg-white p-4 sm:gap-6 sm:p-5" data-testid={`cart-item-${i}`}>
                  <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-black/10 bg-surface sm:h-28 sm:w-28">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display text-base font-bold text-ink sm:text-lg" data-testid={`cart-item-${i}-name`}>
                          {item.name}
                        </h3>
                        {item.detailLines && item.detailLines.length > 0 && (
                          <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1" data-testid={`cart-item-${i}-options`}>
                            {item.detailLines.map((d) => (
                              <li key={d} className="font-mono text-[11px] text-zinc-500">
                                {d}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid={`cart-item-${i}-remove`}
                        aria-label={`Remove ${item.name}`}
                        onClick={() => removeItem(item.key)}
                        className="shrink-0 rounded-[3px] border border-black/10 p-2 text-zinc-500 transition-colors duration-200 hover:border-red-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center rounded-[3px] border border-black/15">
                        <button
                          type="button"
                          data-testid={`cart-item-${i}-qty-dec`}
                          aria-label="Decrease quantity"
                          onClick={() => updateQty(item.key, item.qty - 1)}
                          className="flex h-9 w-9 items-center justify-center text-zinc-500 hover:text-volt transition-colors duration-200"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-8 text-center font-mono text-sm font-bold text-ink" data-testid={`cart-item-${i}-qty`}>
                          {item.qty}
                        </span>
                        <button
                          type="button"
                          data-testid={`cart-item-${i}-qty-inc`}
                          aria-label="Increase quantity"
                          onClick={() => updateQty(item.key, item.qty + 1)}
                          className="flex h-9 w-9 items-center justify-center text-zinc-500 hover:text-volt transition-colors duration-200"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-[11px] text-zinc-500">
                          {formatNZD(item.unitPrice)} each
                        </p>
                        <p className="font-mono text-lg font-bold text-ink" data-testid={`cart-item-${i}-line-total`}>
                          {formatNZD(item.unitPrice * item.qty)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="self-start lg:sticky lg:top-24">
            <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="cart-summary">
              <p className="eyebrow mb-5">Summary</p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-600">Subtotal</span>
                  <span className="font-mono text-ink" data-testid="cart-subtotal">{formatNZD(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-600">{hasKit ? "Shipping (kit, NZ-wide flat)" : "Shipping (accessories, NZ-wide flat)"}</span>
                  <span className="font-mono text-ink" data-testid="cart-shipping">
                    {shipping === null ? "…" : formatNZD(shipping)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-black/10 pt-4 text-base">
                  <span className="font-display font-bold text-zinc-600">Total</span>
                  <span className="font-mono text-xl font-bold text-volt" data-testid="cart-total">
                    {shipping === null ? formatNZD(subtotal) : formatNZD(subtotal + shipping)}
                  </span>
                </div>
              </div>
              <Link
                to="/checkout"
                data-testid="cart-checkout-cta"
                className="group mt-6 flex w-full items-center justify-center gap-2 rounded-[3px] bg-volt px-6 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
              >
                Proceed to Checkout
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <p className="mt-4 text-xs leading-relaxed text-zinc-500">
                {hasKit ? (
                  <>Kit orders ship for a flat $170 NZ-wide — Auckland pickup
                  too. Installation is $200 flat: we build it, you pick it up
                  — and any accessories in this cart ride along free. No card
                  needed, we invoice you manually.</>
                ) : (
                  <>Accessory-only orders ship for a flat $10 NZ-wide. Add a kit
                  and everything in this cart ships free.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
